// getLivestreamComputedData.js
'use strict';
import createCachedSelector from '../resource/createCachedSelector.js';

import getLiveStreamData from '../selector/getLiveStreamData.js';
import getOperationData from '../selector/getOperationData.js';
import getGoalData from '../selector/getGoalData.js';
import getMeData from '../selector/getMeData.js';
import getPrivateShowStatus from '../selector/getPrivateShowStatus.js';

import {
  PRESET_SD,
  PRESET_PREVIEW,
  LivestreamMode,
  ExclusivePrivateStatus,
  StreamType,
  privateShowStatus as privateShowStatusConstant,
} from '../resource/liveStreamConstants.js';

/**
 * Select whether the livestream has a valid stream token.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - livestream id.
 * @return {boolean} True if the livestream has either byteplusToken or streamToken.
 */
const getHasStreamToken = createCachedSelector(
  (state, streamId) =>
    getOperationData(state, ['liveStream', 'paid', streamId], 'byteplusToken'),
  (state, streamId) =>
    getOperationData(state, ['liveStream', 'paid', streamId], 'streamToken'),
  (streamToken, byteplusToken) => !!(streamToken || byteplusToken)
)((state, streamId) => `${streamId}`);

/**
 * Select livestream overlay mode by stream id.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - stream id.
 * @return {string} Livestream mode.
 */
const getLivestreamMode = createCachedSelector(
  (state, streamId) =>
    getLiveStreamData(state, [streamId, 'exclusivePrivate'], 'status'),
  (state, streamId) => getPrivateShowStatus(state, streamId),
  (state, streamId) => getLiveStreamData(state, [streamId], 'status'),
  (exclusivePrivateStatus, privateShowStatus, streamStatus) => {
    if (streamStatus === PRESET_PREVIEW) {
      return LivestreamMode.FREE;
    }
    if (
      [
        ExclusivePrivateStatus.PREPARING,
        ExclusivePrivateStatus.PERFORMING,
      ].includes(exclusivePrivateStatus)
    ) {
      return LivestreamMode.ONE_VS_ONE;
    }

    if (
      [
        privateShowStatusConstant.PREPARING,
        privateShowStatusConstant.PERFORMING,
      ].includes(privateShowStatus)
    ) {
      return LivestreamMode.SHOW;
    }

    if (streamStatus === PRESET_SD) {
      return LivestreamMode.ONE_VS_N;
    }

    return LivestreamMode.FREE;
  }
)((state, streamId) => `${streamId}`);

/**
 * Get should use overlay cta by stream id.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - stream id.
 * @return {boolean} Return true if should use overlay cta.
 */
const getShouldUseOverlayCta = createCachedSelector(
  (state, streamId) => getLivestreamMode(state, streamId),
  (state, streamId) => getHasStreamToken(state, streamId),
  (state, streamId) =>
    getIsMeExclusivePrivatePerformingInitiator(state, streamId),
  (modeName, hasStreamToken, isPerformingInitiator) => {
    const isMode1vsN = LivestreamMode.ONE_VS_N === modeName;
    const isModeShow = LivestreamMode.SHOW === modeName;
    const isMode1vs1 = LivestreamMode.ONE_VS_ONE === modeName;

    if (isMode1vsN && !hasStreamToken) return true;

    if (isModeShow && !hasStreamToken) return true;

    if (isMode1vs1 && isPerformingInitiator && !hasStreamToken) return true;

    return false;
  }
)((state, streamId) => `${streamId}`);

/**
 * Select whether the livestream has any toy device online.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - livestream id.
 * @return {boolean} True if there is at least one device online.
 */
const getIsToyDeviceOnline = createCachedSelector(
  (state, streamId) => getLiveStreamData(state, [streamId], 'device'),
  devices => !!devices && Object.keys(devices).length > 0
)((state, streamId) => `${streamId}`);

/**
 * Select whether the livestream is audio-only.
 *
 * Reads the realtime session value from the liveStream slice, set by the
 * stream.online event while watching in a room.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - livestream id (creator user id).
 * @return {boolean} True if the stream type is audio.
 */
const getIsAudioLive = createCachedSelector(
  (state, streamId) => getLiveStreamData(state, [streamId], 'streamType'),
  streamType => streamType === StreamType.AUDIO
)((state, streamId) => `${streamId}`);

/**
 * Select whether the livestream has top gift data.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - livestream id.
 * @return {boolean} True if top gift data exists.
 * @example
 * getHasLivestreamTopGift(state, streamId); // => true
 */
const getHasLivestreamTopGift = createCachedSelector(
  (state, streamId) => {
    const sessionId = getLiveStreamData(state, [streamId], 'sessionId');
    return getLiveStreamData(state, [
      streamId,
      'session',
      sessionId,
      'topGift',
    ]);
  },
  topGiftData => !!topGiftData && Object.keys(topGiftData).length > 0
)((state, streamId) => `${streamId}`);

/**
 * Select whether the given stream is the currently active (foreground) stream.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - stream id.
 * @return {boolean} True when the stream is the currently active stream.
 * @example
 * getIsStreamActivated(state, 'stream_x'); // => false
 */
const getIsStreamActivated = createCachedSelector(
  state => getOperationData(state, ['liveStream'], 'activeStreamId'),
  (state, streamId) => streamId,
  (activeStreamId, streamId) =>
    activeStreamId != null && streamId != null && activeStreamId === streamId
)((state, streamId) => `${streamId}`);

/**
 * Get the exclusive private viewer-camera redux slice path.
 * @param {object} params
 * @param {string} params.streamId - Stream id.
 * @returns {Array} Viewer-camera slice path.
 * @example
 * getViewerCameraSlicePath({ streamId: 'stream_x' });
 * // => ['liveStream', 'viewerCamera', 'stream_x']
 */
const getViewerCameraSlicePath = ({ streamId }) => [
  'liveStream',
  'viewerCamera',
  streamId,
];

const VIEWER_CAMERA_DATA_DEFAULTS = {
  isPreviewReady: false,
  isPublishing: false,
  isAudioEnabled: true,
  isVideoEnabled: true,
  cameraSwitchVersion: 0,
  isInMainWindow: false,
  canSwitchCamera: true,
  hasMediaAccessError: false,
};

/**
 * Select viewer-camera data with defaults applied when the key is unset.
 * @kind selector
 * @param {Immutable.Map} state - Root state.
 * @param {string} streamId - Stream id.
 * @param {string} dataKey - Viewer-camera data key.
 * @returns {any} Viewer-camera data.
 * @example
 * getViewerCameraData(state, 'stream_x', 'isPublishing'); // => false
 */
const getViewerCameraData = createCachedSelector(
  (state, streamId, dataKey) =>
    getOperationData(state, getViewerCameraSlicePath({ streamId }), dataKey),
  (state, streamId, dataKey) => dataKey,
  (value, dataKey) => value ?? VIEWER_CAMERA_DATA_DEFAULTS[dataKey]
)((state, streamId, dataKey) => `${streamId}:${dataKey}`);

/**
 * Select whether the viewer camera occupies the main window.
 * @kind selector
 * @param {Immutable.Map} state - Root state.
 * @param {string} streamId - Stream id.
 * @returns {boolean} Whether the viewer camera is in the main window.
 * @example
 * getIsExclusiveViewerInMainWindow(state, 'stream_x'); // => false
 */
const getIsExclusiveViewerInMainWindow = createCachedSelector(
  (state, streamId) => getViewerCameraData(state, streamId, 'isPreviewReady'),
  (state, streamId) => getViewerCameraData(state, streamId, 'isInMainWindow'),
  (state, streamId) => getViewerCameraData(state, streamId, 'isVideoEnabled'),
  (isPreviewReady, isInMainWindow, isVideoEnabled) =>
    isPreviewReady && isInMainWindow && isVideoEnabled
)((state, streamId) => `${streamId}`);

/**
 * Check if I (me) am the initiator of the current exclusive private (1-1) goal
 * — the viewer the goal is dedicated to (`exclusive_to`). Status-agnostic: it
 * only checks the current goal, so it is the smallest identity atom the other
 * exclusive-private initiator selectors build on.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - stream id.
 * @return {boolean} true if I am the current exclusive private goal initiator.
 */
const getIsMeExclusivePrivateGoalInitiator = createCachedSelector(
  state => getMeData(state, 'id'),
  (state, streamId) => {
    const currentGoalId = getLiveStreamData(
      state,
      [streamId, 'exclusivePrivate'],
      'currentId'
    );
    return getGoalData(state, [currentGoalId, 'context'], 'exclusive_to');
  },
  (meId, currentExclusiveToUserId) =>
    meId != null && meId === currentExclusiveToUserId
)((state, streamId) => `${streamId}`);

/**
 * Check if I (me) am the initiator of the LAST exclusive private (1-1) goal
 * — the viewer the last goal was dedicated to (`exclusive_to`). Reads `lastId`,
 * used when the trigger goal has already moved there (e.g. ACCEPTED / COOLDOWN).
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - stream id.
 * @return {boolean} true if I am the last exclusive private goal initiator.
 */
const getIsMeExclusivePrivateLastGoalInitiator = createCachedSelector(
  state => getMeData(state, 'id'),
  (state, streamId) => {
    const lastGoalId = getLiveStreamData(
      state,
      [streamId, 'exclusivePrivate'],
      'lastId'
    );
    return getGoalData(state, [lastGoalId, 'context'], 'exclusive_to');
  },
  (meId, lastExclusiveToUserId) =>
    meId != null && meId === lastExclusiveToUserId
)((state, streamId) => `${streamId}`);

/**
 * Check if I (me) am the initiator of a performing exclusive private (1-1)
 * — performing only.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - stream id.
 * @return {boolean} true if I am the initiator of the performing 1-1.
 */
const getIsMeExclusivePrivatePerformingInitiator = createCachedSelector(
  getIsMeExclusivePrivateGoalInitiator,
  (state, streamId) =>
    getLiveStreamData(state, [streamId, 'exclusivePrivate'], 'status'),
  (isGoalInitiator, exclusivePrivateStatus) =>
    isGoalInitiator &&
    exclusivePrivateStatus === ExclusivePrivateStatus.PERFORMING
)((state, streamId) => `${streamId}`);

/**
 * Check if I (me) am the initiator of an ongoing exclusive private (1-1)
 * — preparing or performing.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - stream id.
 * @return {boolean} true if I am the initiator of the ongoing 1-1.
 */
const getIsMeExclusivePrivateOngoingInitiator = createCachedSelector(
  getIsMeExclusivePrivateGoalInitiator,
  (state, streamId) =>
    getLiveStreamData(state, [streamId, 'exclusivePrivate'], 'status'),
  (isGoalInitiator, exclusivePrivateStatus) =>
    isGoalInitiator &&
    [
      ExclusivePrivateStatus.PREPARING,
      ExclusivePrivateStatus.PERFORMING,
    ].includes(exclusivePrivateStatus)
)((state, streamId) => `${streamId}`);

/**
 * Check if I (me) initiated the exclusive private (1-1) across the whole invite
 * lifecycle — considering / accepted / preparing / performing.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} streamId - stream id.
 * @return {boolean} true if I initiated the ongoing 1-1 invite.
 */
const getIsMeExclusivePrivateInviteInitiator = createCachedSelector(
  state => getMeData(state, 'id'),
  getIsMeExclusivePrivateGoalInitiator,
  (state, streamId) =>
    getLiveStreamData(state, [streamId, 'exclusivePrivate'], 'status'),
  (state, streamId) => {
    const lastGoalId = getLiveStreamData(
      state,
      [streamId, 'exclusivePrivate'],
      'lastId'
    );
    return getGoalData(state, [lastGoalId, 'context'], 'exclusive_to');
  },
  (meId, isGoalInitiator, exclusivePrivateStatus, lastExclusiveToUserId) => {
    if (meId == null) return false;
    // ACCEPTED: currentId is cleared and the trigger goal moves to lastId, so read last.
    if (exclusivePrivateStatus === ExclusivePrivateStatus.ACCEPTED)
      return meId === lastExclusiveToUserId;

    return (
      isGoalInitiator &&
      [
        ExclusivePrivateStatus.CONSIDERING,
        ExclusivePrivateStatus.PREPARING,
        ExclusivePrivateStatus.PERFORMING,
      ].includes(exclusivePrivateStatus)
    );
  }
)((state, streamId) => `${streamId}`);

export {
  getHasStreamToken,
  getLivestreamMode,
  getShouldUseOverlayCta,
  getIsToyDeviceOnline,
  getHasLivestreamTopGift,
  getIsAudioLive,
  getIsStreamActivated,
  getViewerCameraSlicePath,
  getViewerCameraData,
  getIsExclusiveViewerInMainWindow,
  getIsMeExclusivePrivateGoalInitiator,
  getIsMeExclusivePrivateLastGoalInitiator,
  getIsMeExclusivePrivatePerformingInitiator,
  getIsMeExclusivePrivateOngoingInitiator,
  getIsMeExclusivePrivateInviteInitiator,
};
